import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ArrowRight, Phone, Mail, MessageCircle, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import seoBg from "../assets/seo-office-bg.webp";
import trelawneyLogo from "@assets/image_1776854359160.webp";
import thatchamLogo from "@assets/image_1776854410430.webp";

export default function SEO() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: "Competitor Analysis",
      description: "Keep your customers close, but your competitors closer! Competitor analysis acts as a window into other SEO strategies, allowing us to determine which websites contain the most value. We use a range of industry leading software to assist us during this process. For instance, did you know that we can look at any website and review its keyword data in relation to current performance? Great data to use when conducting your own keyword research: put simply, your competitors would have gone through the same process when deciding on the keywords to target."
    },
    {
      title: "Website Audit",
      description: "Why is your website not ranking on page one? There are plenty of possibilities such as content, links or even the website itself. By digging deeper into your site, we can pinpoint any problem areas."
    },
    {
      title: "Fix Website Issues",
      description: "Website appearance is just the tip of the iceberg. As a team of keen observers, we can easily identify other problem areas and improve your rankings. Using the website audit, we will fix any issues in order of priority to ensure the quickest return on investment. This is one measure of campaign performance – the audit score will be improved month to month. We have experience of working with website platforms including WordPress, Magento, Joomla, WIX, Bitrix and OpenCart, to name a few."
    }
  ];

  const testimonials = [
    {
      logo: trelawneyLogo,
      text: "I purchased Trelawney Security in Feb 2013, at that time the company had little presence on the Web. Following a re-branding exercise and a new website, it was clear we had to promote the company on the Web. We contacted Page1 to discuss our requirements. It was clear they were customer focused and could do what I wanted. Within 3 months my new website started to appear on the 1st page of Google, within 6 months it was top 1 or 2 on the 1st page.",
      author: "Trelawney Fire & Security"
    },
    {
      logo: thatchamLogo,
      text: "Thatcham engaged with Page1 just a few months ago, but in that time they have lifted the four key search terms we specified from positions in excess of 200 to Page 1. They have made numerous sensible recommendations regarding the setup of our website and even where we haven’t been able to implement a change this has not stopped progress. They provide clear and useful reports each month to show progress. Recommended.",
      author: "Thatcham Research"
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>SEO Service Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="The Cornwall SEO Co. provides tried and tested SEO strategies that will get your business to page one. We work to the Google Guidelines, contact us today." />
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black bg-cover bg-center"
        style={{ backgroundImage: `url(${seoBg})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              Search Engine <br/>
              <span className="text-primary">Optimisation</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Tried and tested SEO strategies that will get your business to page one of the search engines.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Get Your Business <br/> <span className="text-primary">To Page One</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed mb-6">
              It's a fact: few people go to page two of the search engines when looking for a product or service. The Cornwall SEO Co provides tried and tested SEO strategies that will get your business to page one.
            </p>
            <p className="text-black text-xl font-medium leading-relaxed">
              We adhere to Google's strict guidelines to avoid actions that could potentially lead to penalisation, such as link building. From small business to corporates, we can create a unique and personalised SEO strategy, including various approaches.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Our <br/> <span className="text-primary">SEO Process</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl md:text-xl text-black uppercase italic">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-lg md:text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Services Types Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Types of <br/> <span className="text-primary">SEO Services</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed">
              We provide tailored SEO strategies for different business models and markets.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Local SEO",
                description: "Dominate local search results and attract customers in your geographic area with location-based optimisation strategies."
              },
              {
                title: "National SEO",
                description: "Expand your reach across the entire country with comprehensive SEO strategies designed for national competition."
              },
              {
                title: "Ecommerce SEO",
                description: "Increase online sales with specialised ecommerce SEO including product optimisation, category pages, and shopping feeds."
              },
              {
                title: "International SEO",
                description: "Reach global audiences with multi-language and multi-region SEO strategies for international expansion."
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-primary p-6 hover:shadow-[8px_8px_0px_rgba(250,220,32,0.3)] transition-all">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl text-primary uppercase italic">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-sm">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Client <span className="text-primary">Testimonials</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed">
              Real feedback from businesses who trusted us to improve their search visibility and rankings.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.article
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-black text-white border-2 border-primary p-8 md:p-10"
              >
                <div className="flex items-center justify-between gap-8 mb-8">
                  <img src={testimonial.logo} alt={`${testimonial.author} logo`} className="h-20 md:h-24 w-auto max-w-[220px] object-contain bg-white p-3" />
                  <Quote className="text-primary shrink-0" size={34} />
                </div>
                <p className="text-white/85 text-base md:text-lg leading-relaxed italic mb-6">{testimonial.text}</p>
                <p className="text-primary font-black uppercase italic tracking-widest text-sm">{testimonial.author}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-3 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to <br/> Rank?</h3>
                <p className="text-white/80 text-base md:text-xl mb-6 md:mb-12 font-medium">
                  With a diverse range of clients, from SMEs to international corporations; The Cornwall SEO Co provides effective SEO services to improve online visibility and achieve search engine supremacy.
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
